/**
 * Phase 3.5: "Acting as" context.
 *
 * When an admin / AccountManager / SuperAdmin selects a managed user from the
 * header "View as" dropdown, every Schedule / Positions / Orders / Analytics
 * request is re-scoped via a `?acting_as=<user_id>` query param. SuperAdmins
 * additionally get View-as on baskets / allocations / basket-export — those
 * endpoints enforce `super_admin_only=True` server-side.
 *
 * Pool source:
 *  - Admin / AccountManager: their own managed-accounts rows (scoped query).
 *  - SuperAdmin: union of every managed-accounts row across all admins
 *    (?all=true on the list endpoint). v1 omits truly unmanaged users.
 *
 * Persistence:
 *  - Selected user id is stored under localStorage key `acting_as`.
 *  - Rehydrated on app mount.
 *  - Auto-cleared if the selected user is no longer in the pool
 *    (e.g. after detach in another tab) or the caller loses privilege.
 *
 * Users with neither AccountManager nor SuperAdmin membership see an empty
 * pool and `actingAs === null` always.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import managedAccountsService from '../services/managedAccountsService';
import { ManagedAccount } from '../types';

const STORAGE_KEY = 'acting_as';
// Stored alongside the user_id so a page reload still has the human display
// label for a SuperAdmin acting-as target that isn't in their managed pool.
// Cleared whenever actingAs is cleared.
const STORAGE_LABEL_KEY = 'acting_as_label';

/**
 * Wipe both storage keys atomically. The two keys must always move together
 * — leaving an orphan label in localStorage would later re-hydrate without
 * a matching user_id, and leaving the user_id without a label would
 * silently degrade the chip to a UUID. Catch storage errors so Safari
 * private mode / quota-exceeded doesn't crash the React tree.
 */
function clearActingAsStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_LABEL_KEY);
  } catch { /* ignore quota / private mode */ }
}

interface ActingAsContextValue {
  /** The user_id we're acting as, or null when scoped to own account. */
  actingAs: string | null;
  /** Display name for the selected managed user (full_name → email → client_id). */
  actingAsLabel: string | null;
  /**
   * Set or clear the active managed user. Pass null to reset to "My account".
   * Optionally pass `displayLabel` when the caller has a human-readable name
   * in scope (e.g. View-as buttons on Platform PnL / User Roster /
   * Marketplace Analytics that already render the user's full_name). The
   * label is stashed alongside the user_id so the header chip shows a name
   * even for SuperAdmin targets that aren't in `managedUsers`.
   */
  setActingAs: (userId: string | null, displayLabel?: string) => void;
  /** All managed users available to the caller. [] for non-account-managers. */
  managedUsers: ManagedAccount[];
  /** True while the initial managed-accounts fetch is in flight. */
  isLoading: boolean;
  /** Refetch the managed-accounts list (e.g. after attach/detach). */
  refresh: () => Promise<void>;
}

const ActingAsContext = createContext<ActingAsContextValue | undefined>(undefined);

export const ActingAsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAccountManager, isSuperAdmin } = useAuth();
  const hasViewAsPrivilege = isAccountManager || isSuperAdmin;
  const [managedUsers, setManagedUsers] = useState<ManagedAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [actingAs, setActingAsState] = useState<string | null>(() => {
    // Initial hydrate from localStorage. The auto-clear pass runs once the
    // managedUsers list lands, so a stale selection rehydrates briefly but is
    // wiped before any data fetch references it.
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [explicitLabel, setExplicitLabel] = useState<string | null>(() => {
    // Companion label stashed by SuperAdmin View-as buttons (Platform PnL /
    // User Roster / Marketplace Analytics) when they have the user's
    // human-readable name in scope. Persists across reload so the header
    // chip doesn't degrade to "View as: <UUID>" after F5.
    try {
      return localStorage.getItem(STORAGE_LABEL_KEY);
    } catch {
      return null;
    }
  });

  const fetchManagedUsers = useCallback(async () => {
    if (!hasViewAsPrivilege) {
      setManagedUsers([]);
      return;
    }
    setIsLoading(true);
    try {
      // SuperAdmin gets the union of every admin's pool (option 4A) so they
      // can View-as any platform user any Admin/AM has been granted on.
      // Admin/AM get only their own pool — current behavior.
      const list = isSuperAdmin
        ? await managedAccountsService.list({ all: true })
        : await managedAccountsService.list();
      setManagedUsers(list);
    } catch {
      // Silent fail — the dropdown stays empty; auth errors handled upstream.
      setManagedUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [hasViewAsPrivilege, isSuperAdmin]);

  useEffect(() => {
    fetchManagedUsers();
  }, [fetchManagedUsers]);

  // Auto-clear stale selection when the managed pool no longer contains it.
  // EXCEPT for SuperAdmins — they can target any user_id on the platform
  // (the "View as" buttons on Platform PnL / User Roster / Marketplace
  // Analytics pages feed them arbitrary user_ids that are NOT necessarily
  // in any admin's managed-accounts pool). Auto-clearing those selections
  // silently broke View-as for any unmanaged user.
  useEffect(() => {
    if (actingAs && !isSuperAdmin && managedUsers.length > 0) {
      const stillPresent = managedUsers.some((u) => u.managed_user_id === actingAs);
      if (!stillPresent) {
        setActingAsState(null);
        setExplicitLabel(null);
        clearActingAsStorage();
      }
    }
    // When the user loses View-as privilege entirely (no AM, no SuperAdmin),
    // wipe the selection so a downgrade in another tab doesn't leak.
    if (!hasViewAsPrivilege && actingAs) {
      setActingAsState(null);
      setExplicitLabel(null);
      clearActingAsStorage();
    }
  }, [actingAs, managedUsers, hasViewAsPrivilege, isSuperAdmin]);

  const setActingAs = useCallback((userId: string | null, displayLabel?: string) => {
    setActingAsState(userId);
    // Trim the supplied label — call sites often pass `r.full_name || r.email`
    // and we don't want a stray whitespace-only string overriding the
    // managed-user lookup.
    const cleanedLabel = (displayLabel || '').trim() || null;
    setExplicitLabel(userId ? cleanedLabel : null);
    if (!userId) {
      clearActingAsStorage();
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, userId);
      if (cleanedLabel) localStorage.setItem(STORAGE_LABEL_KEY, cleanedLabel);
      else localStorage.removeItem(STORAGE_LABEL_KEY);
    } catch { /* ignore quota / private mode */ }
  }, []);

  const actingAsLabel = useMemo(() => {
    if (!actingAs) return null;
    const match = managedUsers.find((u) => u.managed_user_id === actingAs);
    if (match) return match.full_name || match.email || match.managed_client_id;
    // SuperAdmins can target any user_id on the platform via View-as buttons
    // (Platform PnL / User Roster / Marketplace Analytics). Those buttons
    // already have the user's full_name / email in scope and pass it through
    // `setActingAs(userId, displayLabel)` — use it. Only fall back to the
    // raw UUID when the caller didn't supply a label (legacy or programmatic
    // entry). Admin/AM never see either branch because their auto-clear
    // wipes any unmanaged selection.
    if (explicitLabel) return explicitLabel;
    if (isSuperAdmin) return actingAs;
    return null;
  }, [actingAs, managedUsers, isSuperAdmin, explicitLabel]);

  const value: ActingAsContextValue = useMemo(() => ({
    actingAs,
    actingAsLabel,
    setActingAs,
    managedUsers,
    isLoading,
    refresh: fetchManagedUsers,
  }), [actingAs, actingAsLabel, setActingAs, managedUsers, isLoading, fetchManagedUsers]);

  return <ActingAsContext.Provider value={value}>{children}</ActingAsContext.Provider>;
};

export const useActingAs = (): ActingAsContextValue => {
  const ctx = useContext(ActingAsContext);
  if (!ctx) {
    throw new Error('useActingAs must be used within ActingAsProvider');
  }
  return ctx;
};
